import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom'
import { useEffect, useRef } from 'react'
import { useAuthStore, isPlatformOwner } from './stores/authStore'

// Public pages
import HomePage from './pages/public/HomePage'
import FeaturesPage from './pages/public/FeaturesPage'
import CompliancePage from './pages/public/CompliancePage'
import BillingPage from './pages/public/BillingPage'
import AIGovernancePage from './pages/public/AIGovernancePage'
import SecurityPage from './pages/public/SecurityPage'
import PricingPage from './pages/public/PricingPage'
import ContactPage from './pages/public/ContactPage'

// Auth pages
import LoginPage from './pages/auth/LoginPage'
import ChangePasswordPage from './pages/auth/ChangePasswordPage'

// App pages (protected)
import DashboardPage from './pages/app/DashboardPage'
import AppLayout from './components/layouts/AppLayout'
import ClientsListPage from './pages/app/ClientsListPage'
import ClientDetailPage from './pages/app/ClientDetailPage'
import ClientCreatePage from './pages/app/ClientCreatePage'
import SchedulePage from './pages/app/SchedulePage'
import SessionsListPage from './pages/app/SessionsListPage'
import SessionDetailPage from './pages/app/SessionDetailPage'
import SessionCreatePage from './pages/app/SessionCreatePage'
import NotesListPage from './pages/app/NotesListPage'
import NoteDetailPage from './pages/app/NoteDetailPage'
import TreatmentPlansPage from './pages/app/TreatmentPlansPage'
import TreatmentPlanDetailPage from './pages/app/TreatmentPlanDetailPage'
import AuthorizationsPage from './pages/app/AuthorizationsPage'
import AuthorizationDetailPage from './pages/app/AuthorizationDetailPage'
import BillingHomePage from './pages/app/billing/BillingHomePage'
import ClaimsListPage from './pages/app/billing/ClaimsListPage'
import ClaimDetailPage from './pages/app/billing/ClaimDetailPage'
import ClaimGeneratePage from './pages/app/billing/ClaimGeneratePage'
import PaymentsListPage from './pages/app/billing/PaymentsListPage'
import DenialsListPage from './pages/app/billing/DenialsListPage'
import ComplianceHomePage from './pages/app/compliance/ComplianceHomePage'
import ProfilePage from './pages/app/ProfilePage'
import SettingsPage from './pages/app/SettingsPage'
import ParentDashboardPage from './pages/app/ParentDashboardPage'
import PlatformDashboardPage from './pages/platform/PlatformDashboardPage'
import PlatformTenantDetailPage from './pages/platform/PlatformTenantDetailPage'

// Auth guard component
function RequireAuth() {
  const location = useLocation()
  const { hasHydrated, isAuthenticated, isLoading, checkAuth, forcePasswordChange } = useAuthStore()
  const didCheck = useRef(false)

  useEffect(() => {
    if (!hasHydrated || !isAuthenticated || didCheck.current) return
    didCheck.current = true
    void checkAuth()
  }, [checkAuth, hasHydrated, isAuthenticated])

  if (!hasHydrated) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="h-16 bg-white border-b border-gray-200" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-4">
            <div className="h-8 w-64 bg-gray-200 rounded animate-pulse" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="h-24 bg-gray-200 rounded animate-pulse" />
              <div className="h-24 bg-gray-200 rounded animate-pulse" />
              <div className="h-24 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }

  // Force password change on first login: block all routes except /change-password
  if (forcePasswordChange && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />
  }

  return (
    <>
      {isLoading && (
        <div className="fixed bottom-4 right-4 z-50 rounded-md bg-white border border-gray-200 px-3 py-2 shadow-sm text-sm text-gray-700">
          Syncing...
        </div>
      )}
      <Outlet />
    </>
  )
}

function PlatformGuard() {
  const { user } = useAuthStore()
  if (!user) return <Navigate to="/login" replace />
  if (!isPlatformOwner(user)) {
    return <Navigate to="/app/dashboard" replace />
  }
  return <Outlet />
}

function App() {
  return (
    <Routes>
      {/* Public Routes - SaaS Website */}
      <Route path="/" element={<HomePage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/compliance" element={<CompliancePage />} />
      <Route path="/billing" element={<BillingPage />} />
      <Route path="/ai-governance" element={<AIGovernancePage />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/contact" element={<ContactPage />} />

      {/* Auth Routes */}
      <Route path="/login" element={<LoginPage />} />

      {/* Protected App Routes */}
      <Route element={<RequireAuth />}>
        <Route path="/change-password" element={<ChangePasswordPage />} />
        <Route element={<PlatformGuard />}>
          <Route path="/platform" element={<PlatformDashboardPage />} />
          <Route path="/platform/tenants/:tenantId" element={<PlatformTenantDetailPage />} />
        </Route>
        <Route path="/app" element={<AppLayout />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />

          <Route path="clients" element={<ClientsListPage />} />
          <Route path="clients/new" element={<ClientCreatePage />} />
          <Route path="clients/:clientId" element={<ClientDetailPage />} />

          <Route path="schedule" element={<SchedulePage />} />

          <Route path="sessions" element={<SessionsListPage />} />
          <Route path="sessions/new" element={<SessionCreatePage />} />
          <Route path="sessions/:sessionId" element={<SessionDetailPage />} />

          <Route path="notes" element={<NotesListPage />} />
          <Route path="notes/:noteId" element={<NoteDetailPage />} />

          <Route path="treatment-plans" element={<TreatmentPlansPage />} />
          <Route path="treatment-plans/:planId" element={<TreatmentPlanDetailPage />} />

          <Route path="authorizations" element={<AuthorizationsPage />} />
          <Route path="authorizations/:authorizationId" element={<AuthorizationDetailPage />} />

          <Route path="billing" element={<BillingHomePage />} />
          <Route path="billing/claims" element={<ClaimsListPage />} />
          <Route path="billing/claims/generate" element={<ClaimGeneratePage />} />
          <Route path="billing/claims/:claimId" element={<ClaimDetailPage />} />
          <Route path="billing/payments" element={<PaymentsListPage />} />
          <Route path="billing/denials" element={<DenialsListPage />} />

          <Route path="compliance" element={<ComplianceHomePage />} />

          <Route path="profile" element={<ProfilePage />} />
          <Route path="settings" element={<SettingsPage />} />

          <Route path="my-child" element={<ParentDashboardPage />} />
        </Route>
      </Route>

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

export default App
